export type BusinessStatus = 'open' | 'closing-soon' | 'closed' | 'check-website'

export interface BusinessHoursStatus {
  status: BusinessStatus
  label: 'Open' | 'Closing soon' | 'Closed' | 'Check Website'
}

const DAY_INDEX: Record<string, number> = {
  sun: 0,
  sunday: 0,
  mon: 1,
  monday: 1,
  tue: 2,
  tues: 2,
  tuesday: 2,
  wed: 3,
  wednesday: 3,
  thu: 4,
  thur: 4,
  thurs: 4,
  thursday: 4,
  fri: 5,
  friday: 5,
  sat: 6,
  saturday: 6
}

const DAY_PATTERN =
  '(sun(?:day)?|mon(?:day)?|tue(?:s|sday)?|wed(?:nesday)?|thu(?:r|rs|rsday)?|fri(?:day)?|sat(?:urday)?)'

function easternNow(now: Date): { day: number; minutes: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23'
  }).formatToParts(now)

  const weekday = parts.find(part => part.type === 'weekday')?.value.toLowerCase() || 'sun'
  const hour = Number(parts.find(part => part.type === 'hour')?.value || 0)
  const minute = Number(parts.find(part => part.type === 'minute')?.value || 0)
  return { day: DAY_INDEX[weekday] ?? 0, minutes: hour * 60 + minute }
}

function timeToMinutes(hourText: string, minuteText?: string, meridiem?: string): number {
  let hour = Number(hourText)
  const minute = Number(minuteText || 0)
  const period = meridiem?.toLowerCase()

  if (period === 'pm' && hour !== 12) hour += 12
  if (period === 'am' && hour === 12) hour = 0
  return hour * 60 + minute
}

function segmentAppliesToDay(segment: string, currentDay: number): boolean {
  const normalized = segment
    .toLowerCase()
    .replace(/[–—]/g, '-')
    .replace(/\bthrough\b|\bthru\b|\bto\b/g, '-')

  if (/\b(daily|every day|seven days|7 days)\b/.test(normalized)) return true

  const rangeRegex = new RegExp(`${DAY_PATTERN}\\s*-\\s*${DAY_PATTERN}`, 'gi')
  let rangeMatch: RegExpExecArray | null
  while ((rangeMatch = rangeRegex.exec(normalized))) {
    const start = DAY_INDEX[rangeMatch[1].toLowerCase()]
    const end = DAY_INDEX[rangeMatch[2].toLowerCase()]
    if (start <= end) {
      if (currentDay >= start && currentDay <= end) return true
    } else if (currentDay >= start || currentDay <= end) {
      return true
    }
  }

  const dayRegex = new RegExp(DAY_PATTERN, 'gi')
  const days = [...normalized.matchAll(dayRegex)].map(match => DAY_INDEX[match[1].toLowerCase()])
  return days.length === 0 || days.includes(currentDay)
}

export function getBusinessHoursStatus(
  hours?: string | null,
  now = new Date()
): BusinessHoursStatus {
  const text = hours?.trim()
  if (!text) return { status: 'check-website', label: 'Check Website' }

  const normalized = text.toLowerCase()
  if (/\b(open\s*)?24\s*(hours?|hrs?)\b|\b24\/7\b/.test(normalized)) {
    return { status: 'open', label: 'Open' }
  }

  if (
    /\b(appointment|call|website|varies|vary|seasonal|contact|unknown|not available)\b/.test(
      normalized
    )
  ) {
    return { status: 'check-website', label: 'Check Website' }
  }

  const { day, minutes: currentMinutes } = easternNow(now)
  const segments = text.split(/[;\n|]+/).map(segment => segment.trim()).filter(Boolean)
  let foundAnyParsableHours = false
  let foundTodayRange = false
  let explicitlyClosedToday = false
  let appliesToday = false

  for (const segment of segments) {
    const normalizedSegment = segment.replace(/[–—]/g, '-')
    const timeRange =
      /(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\s*(?:-|to)\s*(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/i.exec(
        normalizedSegment
      )
    if (timeRange) foundAnyParsableHours = true
    if (!segmentAppliesToDay(segment, day)) continue
    appliesToday = true
    if (/\bclosed\b/i.test(segment)) {
      explicitlyClosedToday = true
      continue
    }
    if (!timeRange) continue

    foundTodayRange = true
    const startHour = Number(timeRange[1])
    const endHour = Number(timeRange[4])
    const suppliedStartPeriod = timeRange[3]?.toLowerCase()
    const suppliedEndPeriod = timeRange[6]?.toLowerCase()
    // Infer omitted periods for common shorthand such as "9-5pm" and "9am-5".
    const startPeriod =
      suppliedStartPeriod ||
      (suppliedEndPeriod === 'pm' && startHour > endHour ? 'am' : suppliedEndPeriod) ||
      (startHour > endHour ? 'am' : undefined)
    const endPeriod =
      suppliedEndPeriod ||
      (suppliedStartPeriod === 'am' && endHour <= startHour ? 'pm' : suppliedStartPeriod) ||
      (endHour <= startHour ? 'pm' : undefined)
    const opens = timeToMinutes(timeRange[1], timeRange[2], startPeriod)
    const closes = timeToMinutes(timeRange[4], timeRange[5], endPeriod)

    if (currentMinutes >= opens && currentMinutes < closes) {
      const minutesUntilClose = closes - currentMinutes
      return minutesUntilClose < 60
        ? { status: 'closing-soon', label: 'Closing soon' }
        : { status: 'open', label: 'Open' }
    }
  }

  if (explicitlyClosedToday || foundTodayRange || (foundAnyParsableHours && !appliesToday)) {
    return { status: 'closed', label: 'Closed' }
  }
  return { status: 'check-website', label: 'Check Website' }
}
